import { lazy, Suspense, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FileText, Upload, Download, Trash2, History, X, FileOutput, Pencil } from "lucide-react";
import {
  useClientDocuments,
  useUploadDocument,
  useDeleteDocument,
  useConvertDocumentToPdf,
  downloadDocumentBytes,
  fetchDocumentBytes,
  formatFileSize,
  isPdfConvertible,
  DOCUMENT_CATEGORY_LABEL,
  DocumentCategory,
  ClientDocumentRecord,
} from "@/api/documents";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import { useDocumentOcrExtraction, OCR_STATUS_LABEL, OCR_STATUS_BADGE_CLASS } from "@/api/ocr";
import OcrExtractionModal from "@/components/ocr/OcrExtractionModal";

// Editor en el sistema (misma pareja de componentes que ya usa Mesa de
// Trabajo, generalizados para aceptar fetch/save inyectados en vez de los
// hooks fijos de workbench — ver ExcelEditorModal.tsx/WordEditorModal.tsx).
// Carga diferida: son librerías pesadas (exceljs / mammoth+tiptap+docx) que
// no deben ir en el bundle inicial de la ficha del cliente.
const ExcelEditorModal = lazy(() => import("@/pages/workbenches/ExcelEditorModal"));
const WordEditorModal = lazy(() => import("@/pages/workbenches/WordEditorModal"));

const CATEGORIES = Object.keys(DOCUMENT_CATEGORY_LABEL) as DocumentCategory[];

const OCR_SUPPORTED_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function DocumentsSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | "">("");
  const { data, isLoading } = useClientDocuments(clientId, { category: categoryFilter || undefined });
  const [showUpload, setShowUpload] = useState(false);
  const [versioningDoc, setVersioningDoc] = useState<ClientDocumentRecord | null>(null);
  const [editingDoc, setEditingDoc] = useState<ClientDocumentRecord | null>(null);
  const uploadDocument = useUploadDocument();

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">Documentos</h2>
        <div className="flex items-center gap-2">
          <select
            className="input-field !py-1 !text-xs"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as DocumentCategory | "")}
          >
            <option value="">Todas las categorías</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {DOCUMENT_CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
          {hasPermission("documents.create") && (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1 !py-1 !text-xs"
              onClick={() => setShowUpload(true)}
            >
              <Upload size={13} /> Subir documento
            </button>
          )}
        </div>
      </div>

      <div className="mt-4">
        {isLoading && <p className="text-sm text-gray-400">Cargando documentos...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene documentos cargados.</p>
        )}
        {!isLoading && data?.data && data.data.length > 0 && (
          <div className="space-y-2">
            {data.data.map((doc) => (
              <DocumentRow
                key={doc.id}
                clientId={clientId}
                document={doc}
                canDelete={hasPermission("documents.delete")}
                canDownload={hasPermission("documents.download")}
                canVersion={hasPermission("documents.create")}
                onVersion={() => setVersioningDoc(doc)}
                onEdit={() => setEditingDoc(doc)}
              />
            ))}
          </div>
        )}
      </div>

      {showUpload && <UploadDocumentModal clientId={clientId} onClose={() => setShowUpload(false)} />}
      {versioningDoc && (
        <UploadDocumentModal
          clientId={clientId}
          replacesDocument={versioningDoc}
          onClose={() => setVersioningDoc(null)}
        />
      )}

      {/*
        Editor en el sistema para documentos de cliente (a pedido explícito
        del usuario: quería la misma opción de "ver/editar como en Excel"
        que ya existe en Mesa de Trabajo, también en la ficha del cliente).
        Reusa los mismos componentes de edición inyectando fetch/save contra
        el endpoint de documentos del cliente en vez del de workbench — al
        guardar se crea una nueva versión del mismo documento (mismo flujo
        que "Subir nueva versión"), no se sobrescribe en el sitio.
      */}
      {editingDoc && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <p className="rounded-md bg-white px-4 py-2 text-sm text-gray-600 shadow">Cargando editor...</p>
            </div>
          }
        >
          {editingDoc.mimeType === XLSX_MIME ? (
            <ExcelEditorModal
              fileName={editingDoc.originalName}
              fetchBytes={() => fetchDocumentBytes(clientId, editingDoc.id)}
              onSave={async (blob, filename) => {
                await uploadDocument.mutateAsync({
                  clientId,
                  file: new File([blob], filename, { type: blob.type }),
                  category: editingDoc.category,
                  replacesDocumentId: editingDoc.id,
                });
              }}
              onClose={() => setEditingDoc(null)}
            />
          ) : (
            <WordEditorModal
              fileName={editingDoc.originalName}
              fetchBytes={() => fetchDocumentBytes(clientId, editingDoc.id)}
              onSave={async (blob, filename) => {
                await uploadDocument.mutateAsync({
                  clientId,
                  file: new File([blob], filename, { type: blob.type }),
                  category: editingDoc.category,
                  replacesDocumentId: editingDoc.id,
                });
              }}
              onClose={() => setEditingDoc(null)}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}

function DocumentRow({
  clientId,
  document,
  canDelete,
  canDownload,
  canVersion,
  onVersion,
  onEdit,
}: {
  clientId: string;
  document: ClientDocumentRecord;
  canDelete: boolean;
  canDownload: boolean;
  canVersion: boolean;
  onVersion: () => void;
  onEdit: () => void;
}) {
  const deleteDocument = useDeleteDocument();
  const convertToPdf = useConvertDocumentToPdf();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDocumentBytes(clientId, document.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const handleConvertToPdf = async () => {
    try {
      await convertToPdf.mutateAsync({ clientId, documentId: document.id });
      toast.success("Se generó el PDF y quedó guardado en el expediente.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar el documento "${document.originalName}"?`)) return;
    try {
      await deleteDocument.mutateAsync({ clientId, documentId: document.id });
      toast.success("Documento eliminado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-100 p-3">
      <div className="flex items-center gap-3 overflow-hidden">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
          <FileText size={16} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">
            {document.originalName}
            {document.version > 1 && (
              <span className="ml-1 text-xs font-normal text-gray-400">v{document.version}</span>
            )}
          </p>
          <p className="text-xs text-gray-500">
            {DOCUMENT_CATEGORY_LABEL[document.category]} · {formatFileSize(document.sizeBytes)}
            {document.status === "ARCHIVED" && <span className="ml-1 text-amber-600">· Reemplazado</span>}
            {document.expirationDate && <span className="ml-1">· Vence {document.expirationDate}</span>}
          </p>
          {OCR_SUPPORTED_MIME_TYPES.has(document.mimeType) && <OcrBadge clientId={clientId} document={document} />}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {canVersion && (document.mimeType === XLSX_MIME || document.mimeType === DOCX_MIME) && (
          <button
            type="button"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-50 hover:text-brand-600"
            title="Ver y editar en el sistema"
            onClick={onEdit}
          >
            <Pencil size={15} />
          </button>
        )}
        {canVersion && (
          <button
            type="button"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
            title="Subir nueva versión"
            onClick={onVersion}
          >
            <History size={15} />
          </button>
        )}
        {canDownload && (
          <button
            type="button"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-50 hover:text-brand-600 disabled:opacity-50"
            title="Descargar"
            onClick={handleDownload}
            disabled={downloading}
          >
            <Download size={15} />
          </button>
        )}
        {canDownload && isPdfConvertible(document.mimeType) && (
          <button
            type="button"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-50 hover:text-brand-600 disabled:opacity-50"
            title="Convertir a PDF"
            onClick={handleConvertToPdf}
            disabled={convertToPdf.isPending}
          >
            <FileOutput size={15} />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
            title="Eliminar"
            onClick={handleDelete}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Fase 22: insignia con el estado de la extracción OCR del documento (solo
 * se renderiza para tipos de imagen soportados). Al hacer clic abre el
 * mismo modal de revisión que usa la bandeja global /ocr-review. */
function OcrBadge({ clientId, document }: { clientId: string; document: ClientDocumentRecord }) {
  const { data: extraction } = useDocumentOcrExtraction(clientId, document.id);
  const [showModal, setShowModal] = useState(false);

  if (!extraction) {
    return <span className="ml-1 text-gray-400">· OCR procesando...</span>;
  }

  return (
    <>
      <button
        type="button"
        className={`ml-1 rounded-full px-1.5 py-0 text-[11px] ${OCR_STATUS_BADGE_CLASS[extraction.status]}`}
        onClick={() => setShowModal(true)}
      >
        OCR: {OCR_STATUS_LABEL[extraction.status]}
      </button>
      {showModal && <OcrExtractionModal extractionId={extraction.id} onClose={() => setShowModal(false)} />}
    </>
  );
}

function UploadDocumentModal({
  clientId,
  replacesDocument,
  onClose,
}: {
  clientId: string;
  replacesDocument?: ClientDocumentRecord;
  onClose: () => void;
}) {
  const uploadDocument = useUploadDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<DocumentCategory>(replacesDocument?.category ?? "OTROS");
  const [documentDate, setDocumentDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [tags, setTags] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Debe seleccionar un archivo.");
      return;
    }
    try {
      await uploadDocument.mutateAsync({
        clientId,
        file,
        category,
        documentDate: documentDate || undefined,
        expirationDate: expirationDate || undefined,
        tags: tags || undefined,
        replacesDocumentId: replacesDocument?.id,
      });
      toast.success(replacesDocument ? "Nueva versión subida correctamente." : "Documento subido correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {replacesDocument ? `Nueva versión de "${replacesDocument.originalName}"` : "Subir documento"}
          </h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="document-file" className="block text-sm font-medium text-gray-700">
              Archivo
            </label>
            <input id="document-file" ref={fileInputRef} type="file" className="input-field mt-1" required />
          </div>
          {!replacesDocument && (
            <div>
              <label htmlFor="document-category" className="block text-sm font-medium text-gray-700">
                Categoría
              </label>
              <select
                id="document-category"
                className="input-field mt-1"
                value={category}
                onChange={(e) => setCategory(e.target.value as DocumentCategory)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {DOCUMENT_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="document-date" className="block text-sm font-medium text-gray-700">
                Fecha del documento
              </label>
              <input
                id="document-date"
                type="date"
                className="input-field mt-1"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="document-expiration" className="block text-sm font-medium text-gray-700">
                Vencimiento
              </label>
              <input
                id="document-expiration"
                type="date"
                className="input-field mt-1"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="document-tags" className="block text-sm font-medium text-gray-700">
              Etiquetas (separadas por coma)
            </label>
            <input id="document-tags" className="input-field mt-1" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={uploadDocument.isPending}>
              {uploadDocument.isPending ? "Subiendo..." : "Subir"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
